import { SCHOOL_TYPE_FIELD } from '../lib/finderFields';

/**
 * A Finder grade option, configured in Settings › Finder. `value` is the grade
 * key used for filtering and `label` is what families see. The array order is
 * the display order.
 */
export interface GradeOption {
  value: string;
  label: string;
}

/** Surfaces a configured info section can appear on. Distance is always shown. */
export type SectionSurface = 'card' | 'filter' | 'compare';

export const ALL_SURFACES: SectionSurface[] = ['card', 'filter', 'compare'];

/**
 * Admin-authored help text for one choice-field option, shown as a tooltip next
 * to the value (e.g. school type "choice" -> "applications open April 1").
 * Stored as a list (not a value-keyed map) so the client's response
 * key-camelization can't mangle slug values like `out_of_district`.
 */
export interface OptionDescription {
  value: string;
  description: string;
}

/** A choice-field option's value→label pair, sent with field-backed sections. */
export interface SectionOption {
  value: string;
  label: string;
}

/**
 * A config-driven per-school info section. Each entry maps a SchoolFinderV3 form
 * field (`source: 'field'`) or a computed built-in (`source: 'builtin'`, e.g.
 * seat availability) onto the card/filter/compare surfaces, with an
 * admin-authored label and an MUI icon name. Built from `finder_school_sections`.
 */
export interface SchoolFieldSection {
  key: string;
  source: 'field' | 'builtin';
  /** Present when `source === 'field'`: the SchoolFinderV3 `field_name`. */
  field?: string;
  /** Present when `source === 'field'`: the form field type (drives rendering). */
  type?: string;
  label: string;
  /** MUI icon component name (e.g. 'AccessTime'); may be empty. */
  icon: string;
  surfaces: SectionSurface[];
  /** Per-option tooltip text for choice fields; absent when none configured. */
  optionDescriptions?: OptionDescription[];
  /** Choice-field value→label options; absent otherwise. */
  options?: SectionOption[];
}

export const DEFAULT_SCHOOL_SECTIONS: SchoolFieldSection[] = [
  {
    key: 'school_hours',
    source: 'field',
    field: 'school_hours',
    type: 'text',
    label: 'School Hours',
    icon: 'AccessTime',
    surfaces: ['card', 'compare'],
  },
  {
    key: 'total_enrollment',
    source: 'field',
    field: 'total_enrollment',
    type: 'text',
    label: 'Students',
    icon: 'GroupsOutlined',
    surfaces: ['card', 'compare'],
  },
  {
    key: SCHOOL_TYPE_FIELD,
    source: 'field',
    field: SCHOOL_TYPE_FIELD,
    type: 'select',
    label: 'School Type',
    icon: 'CategoryOutlined',
    surfaces: ['card', 'filter', 'compare'],
  },
  {
    key: 'program_types',
    source: 'field',
    field: 'program_types',
    type: 'checkbox',
    label: 'Program Types',
    icon: 'MenuBook',
    surfaces: ['card', 'filter', 'compare'],
  },
  {
    key: 'seat_availability',
    source: 'builtin',
    label: 'Seat Availability',
    icon: 'EventSeat',
    surfaces: ['card', 'filter', 'compare'],
  },
];

/**
 * A distance ring around the searched address. `miles` is the radius; the
 * legend text auto-formats via the `finder.distance_ring_label_*` translation.
 * An empty list falls back to the defaults.
 */
export interface MapRing {
  miles: number;
}

/** Rings shown when an org hasn't customized them (mirrors the BE default). */
export const DEFAULT_MAP_RINGS: MapRing[] = [{ miles: 1 }, { miles: 1.5 }];

/** Shape of GET /finder/config. Per-org boot config. */
export interface FinderConfig {
  organization: string;
  orgName: string | null;
  placeholderAddress: string;
  primaryColor: string;
  secondaryColor: string;
  logoUrl: string | null;
  faviconUrl: string | null;
  heroImageUrl: string | null;
  gradesEnabled: GradeOption[];
  languagesEnabled: string[];
  schoolSections: SchoolFieldSection[];
  mapRings: MapRing[];
  isPublished: boolean;
  snapToStreet: boolean;
}

export const DEFAULT_CONFIG: FinderConfig = {
  organization: '',
  orgName: null,
  placeholderAddress: 'Enter an address',
  primaryColor: '#4A5563',
  secondaryColor: '#EE7D3A',
  logoUrl: null,
  faviconUrl: null,
  heroImageUrl: null,
  gradesEnabled: [],
  languagesEnabled: ['en'],
  schoolSections: DEFAULT_SCHOOL_SECTIONS,
  mapRings: DEFAULT_MAP_RINGS,
  isPublished: true,
  snapToStreet: false,
};
